import type { AutoTask } from '@/lib/autoTasks';
import type { AutoTaskState } from '@/lib/dataSync';
import { isStaleHarvestTask } from '@/lib/guidance/guidanceContext';

// ── Storage keys ──────────────────────────────────────────────────────────────

export const TASK_RECURRENCE_KEY = 'garden-task-recurrence-v1';
export const GUIDANCE_SHOWN_KEY = 'garden-guidance-shown-v1';
export const GUIDANCE_SHOWN_TTL_MS = 24 * 60 * 60 * 1000;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TaskRecurrenceEntry {
  lastSeenAt?: string;
  lastDismissedAt?: string;
  lastCompletedAt?: string;
}

export type TaskRecurrenceState = Record<string, TaskRecurrenceEntry>;

export interface GuidanceShownEntry { id: string; shownAt: number }

// ── localStorage helpers ──────────────────────────────────────────────────────

export function readTaskRecurrenceState(): TaskRecurrenceState {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(TASK_RECURRENCE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as TaskRecurrenceState;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function writeTaskRecurrenceState(state: TaskRecurrenceState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(TASK_RECURRENCE_KEY, JSON.stringify(state));
  } catch { /* quota — fail silently */ }
}

export function updateTaskRecurrence(id: string, patch: TaskRecurrenceEntry): void {
  const state = readTaskRecurrenceState();
  state[id] = { ...(state[id] ?? {}), ...patch };
  writeTaskRecurrenceState(state);
}

export function readRecentlyShownIds(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(GUIDANCE_SHOWN_KEY);
    if (!raw) return new Set();
    const entries = JSON.parse(raw) as GuidanceShownEntry[];
    const cutoff = Date.now() - GUIDANCE_SHOWN_TTL_MS;
    return new Set(entries.filter(e => e.shownAt > cutoff).map(e => e.id));
  } catch { return new Set(); }
}

export function recordShownIds(ids: string[]): void {
  if (typeof window === 'undefined' || ids.length === 0) return;
  try {
    const raw = localStorage.getItem(GUIDANCE_SHOWN_KEY);
    const existing: GuidanceShownEntry[] = raw ? (JSON.parse(raw) as GuidanceShownEntry[]) : [];
    const cutoff = Date.now() - GUIDANCE_SHOWN_TTL_MS;
    const pruned = existing.filter(e => e.shownAt > cutoff && !ids.includes(e.id));
    const updated = [...pruned, ...ids.map(id => ({ id, shownAt: Date.now() }))];
    localStorage.setItem(GUIDANCE_SHOWN_KEY, JSON.stringify(updated));
  } catch { /* quota — fail silently */ }
}

// ── Cooldown helpers ──────────────────────────────────────────────────────────

export function daysSinceIso(timestamp: string | undefined, today: string): number | null {
  if (!timestamp) return null;
  const iso = timestamp.slice(0, 10);
  return Math.max(0, Math.round((new Date(today).getTime() - new Date(iso).getTime()) / 86400000));
}

export function dismissedCooldownDays(task: AutoTask): number {
  if (task.priorityLevel === 'critical') return Math.min(task.cooldownDays ?? 1, 1);
  if (task.priorityLevel === 'important') return Math.max(task.cooldownDays ?? 5, 5);
  return Math.max(task.cooldownDays ?? 21, task.category === 'mulching' ? 30 : 21);
}

export function completedCooldownDays(task: AutoTask): number {
  if (task.priorityLevel === 'critical') return 1;
  return task.repeatAfterDays ?? task.cooldownDays ?? 7;
}

// ── Primary suppression check ─────────────────────────────────────────────────

// Guidance guardrail: Dashboard, Calendar, and Plant Detail must use
// shouldShowTask for active auto-task visibility. Do not hand-roll suppression.
export function isAutoTaskSuppressed(
  task: AutoTask,
  today: string,
  autoChecked: Set<string>,
  autoDismissed: Set<string>,
  autoArchived: Record<string, string>,
  recurrence: TaskRecurrenceState,
): boolean {
  if (autoChecked.has(task.id)) return true;

  const entry = recurrence[task.id];
  if (autoDismissed.has(task.id)) {
    const dismissedDays = daysSinceIso(entry?.lastDismissedAt, today);
    if (dismissedDays === null) return task.priorityLevel !== 'critical';
    return dismissedDays < dismissedCooldownDays(task);
  }

  const archivedAt = autoArchived[task.id];
  if (archivedAt) {
    const completedDays = daysSinceIso(entry?.lastCompletedAt ?? archivedAt, today);
    if (completedDays === null) return true;
    return completedDays < completedCooldownDays(task);
  }

  return false;
}

export interface ShouldShowTaskOptions {
  recurrence?: TaskRecurrenceState;
  surface?: 'dashboard' | 'calendar' | 'plant-detail' | 'other';
}

export function shouldShowTask(
  task: AutoTask,
  state: AutoTaskState | null | undefined,
  today: string,
  options: ShouldShowTaskOptions = {},
): boolean {
  if (isStaleHarvestTask(task, today)) return false;

  const autoChecked = new Set(state?.autoChecked ?? []);
  const autoDismissed = new Set(state?.autoDismissed ?? []);
  const autoArchived = state?.autoArchived ?? {};
  const recurrence = options.recurrence ?? readTaskRecurrenceState();

  return !isAutoTaskSuppressed(
    task,
    today,
    autoChecked,
    autoDismissed,
    autoArchived,
    recurrence,
  );
}
